import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

/**
 * How many accounts are waiting for approval, shared by the queue screen and
 * the navigation badge.
 *
 * A context rather than a callback prop threaded through the shell, and set as
 * a RESULT of loading rather than synchronised by an effect. The effect version
 * — `useEffect(() => onCountChange(rows.length), [rows.length])` — is what
 * `react-hooks/set-state-in-effect` objects to, and the objection is right: the
 * count changes because a fetch returned or an operator approved someone, both
 * of which are events. Deriving it from a render is a second source of truth
 * that can disagree with the first for a frame.
 *
 * `null` means "not loaded yet", which the badge renders as nothing rather than
 * as a confident zero.
 */
interface PendingCountValue {
  count: number | null
  setCount: (n: number | null) => void
}

const PendingCountContext = createContext<PendingCountValue | null>(null)

export function PendingCountProvider({ children }: { children: ReactNode }) {
  const [count, setCountState] = useState<number | null>(null)
  const setCount = useCallback((n: number | null) => setCountState(n), [])
  const value = useMemo(() => ({ count, setCount }), [count, setCount])
  return <PendingCountContext value={value}>{children}</PendingCountContext>
}

/**
 * The no-provider answer, as a MODULE CONSTANT rather than a fresh object.
 *
 * Outside the provider the badge simply has nothing to show, which is the right
 * answer on the auth surfaces rather than a crash. Building that answer inline
 * made a new `setCount` on every call, and `ApprovalQueue`'s first-page effect
 * lists `setCount` among its dependencies — so a consumer rendered without a
 * provider re-ran that effect on every render, re-fetching the queue and, since
 * each failure sets a fresh error object, re-rendering again. Measured at three
 * to six runs where one was intended.
 *
 * The provider's own `setCount` is `useCallback([])` and was always stable, so
 * nothing shipped was looping. This closes the gap between the two paths rather
 * than leaving one of them a trap.
 */
const NO_PROVIDER: PendingCountValue = { count: null, setCount: () => {} }

export function usePendingCount(): PendingCountValue {
  return useContext(PendingCountContext) ?? NO_PROVIDER
}
